<!-- DO NOT REMOVE THIS COMPONENT

This is a reusable modal window popup that can take in multiple parameters
to customize a popup window that will cover the entire screen.

To use this component in App.vue:
  1) Run this.resetmodalData();
  2) Set this.$store.state.modalData = { parameter1: value1, parameter2: value2 }
      - Possible parameters are described below
  3) Run this.$nextTick(() => { eventBus.$emit("openModalWindow") })

*All parameters have default values, so if they are not being used, they need not be mentioned
    in the above statement

Parameters include:
- headerText (string)
- bodyText (string)
    - add [newline] in the string to move to a new line
        - using this will default text-alignment to left
- centerBodyText (bool)
    - Defaults to left align if not specified
- entryField1Exists (bool)
- entryField2Exists (bool)
    - can also be a password using the next optional boolean v
- entry1IsPassword (bool)    
- entry2IsPassword (bool)
    - Automatically hides the characters as they are typed
    - If both entries are passwords, a SHOW/HIDE button will appear next to them
- entry1ShowHide (bool)
- entry2ShowHide (bool)
    - If true, the field will include a small show/hide option next to it
    - If using Microsoft Edge, passwords already include a show/hide option
- checkboxExists (bool)
    - Adds a checkbox that's value will be passed into the 3rd place in the acceptMethod
- checkboxText (string)
    - This is the text that will be beside the checkbox, (i.e., [] Checkbox Text)
- checkboxValue (bool)
    - Defaults to false, will start as checked if set to true
- closeButtonExists (bool)
    - true by default
- closeButtonText (string)
- acceptButton1Exists (bool)
- acceptButton2Exists (bool)
- acceptButton1Text (string)
- acceptButton2Text (string)
- acceptMethod1 (function)
- acceptMethod2 (function(userResponse1, userResponse2) => {METHOD HERE})
    - userResponse and userResponse2 are optional and take the user input from the entryField(s)
    - userResponse2 cannot be accessed unless userResponse1 is also being used
    - This method runs when the user hits the accept button
    - Accept method can also take no inputs, which looks like... acceptMethod: () => {METHOD HERE}
- tableExists (bool)
    - This allows the modal window to run a for loop over an array of information
- tableData (string[string[]])
    - Each string[] in the array will be on its own line
- tableRowSeperator
    - This is what goes between values in each row
    - For example, if set to "--" with values [1, 2, 3, 4] results in: 1--2--3--4
 -->

<template>
  <div v-if="modalWindowOpen" class="modal-background">
    <div id="modal-window">
      <div class="modal-header">
        {{ headerText }}
      </div>
      <div v-if="!bodyContainsBreaks" class="modal-body">
        {{ bodyText }}
      </div>
      <div
        class="modal-body"
        v-else
        v-for="line in bodyText"
        :key="bodyText[bodyText.indexOf(line)]"
        :class="{ leftalign: !centerBodyText, centeralign: centerBodyText }"
      >
        {{ bodyText[bodyText.indexOf(line)] }}
      </div>
      <div v-if="checkboxExists">
      <input type="checkbox" id="modalCheckbox" name="checkbox1" v-model="checkboxValue"/>
      <label for="checkbox1" id="modalCheckboxText">{{checkboxText}}</label>
      </div>
      <br />
      <!-- HERE IS THE NEW TABLE -->
      <div class="modal-table" v-if="tableExists">
        <div v-for="row in tableData" :key="row[0] + row[1]">
          <div>{{ row[0] + tableRowSeperator + row[1] }}</div>
          <br />
        </div>
      </div>
      <!-- HERE ENDS THE NEW TABLE -->
      <br />
      <div id="selector" v-if="modalSelectExists">
        <select name="modalSelect" id="modalSelect" v-model="modalSelectValue">
          <option v-for="option in modalSelectOptions" :key="option">{{ option }}</option>
          </select>
      </div>
      <div :class="{ modalCenterPass: entry1ShowHide }">
        <input
          v-if="entryFieldExists && !entry1IsPassword"
          type="text"
          class="modal-entry"
          v-model="userResponse"
          v-on:keydown.enter="modalAcceptMethod"
          ref="modalInput1"
          :placeholder="entryFieldText"
        />
        <input
          v-if="entryFieldExists && entry1IsPassword"
          type="password"
          class="modal-entry modal-password"
          id="modal-entry-1"
          v-model="userResponse"
          v-on:keydown.enter="modalAcceptMethod"
          ref="modalInput1"
          :placeholder="entryFieldText"
        />
        <button
          v-if="entryFieldExists && entry1IsPassword && entry1ShowHide"
          @click="revealPassword('modal-entry-1')"
          class="modal-pass-reveal"
          v-tooltip="'Show/Hide'"
          tabindex="-1"
        >
          &lt;{{ showHideDisplay1 }}&gt;
        </button>
      </div>
      <div :class="{ modalCenterPass: entry2ShowHide }">
        <input
          v-if="entryField2Exists && !entry2IsPassword"
          type="text"
          class="modal-entry"
          v-model="userResponse2"
          v-on:keydown.enter="modalAcceptMethod"
          :placeholder="entryField2Text"
        />
        <input
          v-if="entryField2Exists && entry2IsPassword"
          type="password"
          class="modal-entry modal-password"
          id="modal-entry-2"
          v-model="userResponse2"
          v-on:keydown.enter="modalAcceptMethod"
          :placeholder="entryField2Text"
        />
        <button
          v-if="
            entryField2Exists &&
            entry2IsPassword &&
            (entry1IsPassword || entry2ShowHide)
          "
          @click="revealPassword('modal-entry-2')"
          class="modal-pass-reveal"
          v-tooltip="'Show/Hide'"
          tabindex="-1"
        >
          &lt;{{ showHideDisplay2 }}&gt;
        </button>
      </div>
      <div class="modal-button">
        <button
          v-if="acceptButtonExists"
          @click="modalAcceptMethod"
          v-on:keydown.enter="modalAcceptMethod"
          ref="modalAccept1"
        >
          {{ acceptButtonText }}
        </button>
        <button v-if="acceptButton2Exists" @click="modalAcceptMethod2">
          {{ acceptButton2Text }}
        </button>
        <button v-if="closeButtonExists" @click="closeModalWindow">
          {{ closeButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { eventBus } from "../main.js";

export default {
  data() {
    return {
      //Start of customizable modal window options
      //Assign these using this.$store.state.modalData = { headerText: "Header", bodyText: "Body"}
      headerText: "DEFAULT HEADER",
      bodyText: "DEFAULT BODY",
      acceptButtonText: "Ok",
      closeButtonText: "Close",
      closeButtonExists: true,
      acceptButtonExists: false,
      acceptButton2Exists: false,
      entry1IsPassword: false,
      entry1ShowHide: false,
      entryFieldExists: false,
      entryField2Exists: false,
      modalWindowOpen: false,
      //End of modal window options
      userResponse: "",
      userResponse2: "",
      bodyContainsBreaks: false,
      centerBodyText: false,
      entry2IsPassword: false,
      entry2ShowHide: false,
      entryFieldText: "",
      entryField2Text: "",
      tableExists: false,
      tableData: [],
      tableRowSeperator: "",
      showHideDisplay1: "O",
      showHideDisplay2: "O",
      checkboxText: "",
      checkboxExists: false,
      checkboxValue: false,
      modalSelectValue: "",
      modalSelectOptions: [],
      modalSelectExists: false,
    };
  },
  methods: {
    updateModaldata() {
      Object.entries(this.$store.state.modalWindow).forEach(
        (modalComponent) => {
          //   if (modalComponent[1] != "") {
          switch (modalComponent[0]) {
            case "headerText":
              this.headerText = modalComponent[1];
              break;
            case "bodyText":
              if (modalComponent[1].includes("[newline]")) {
                this.bodyText = modalComponent[1].split("[newline]");
                this.bodyContainsBreaks = true;
              } else {
                this.bodyText = modalComponent[1];
                this.bodyContainsBreaks = false;
              }
              break;
            case "acceptButton1Text":
              this.acceptButtonText = modalComponent[1];
              break;
            case "acceptButton2Text":
              this.acceptButton2Text = modalComponent[1];
              break;
            case "closeButtonText":
              this.closeButtonText = modalComponent[1];
              break;
            case "closeButtonExists":
              this.closeButtonExists = modalComponent[1];
              break;
            case "acceptButton1Exists":
              this.acceptButtonExists = modalComponent[1];
              break;
            case "acceptButton2Exists":
              this.acceptButton2Exists = modalComponent[1];
              break;
            case "entryField1Exists":
              this.entryFieldExists = modalComponent[1];
              break;
            case "entryField2Exists":
              this.entryField2Exists = modalComponent[1];
              break;
            case "centerBodyText":
              this.centerBodyText = modalComponent[1];
              break;
            case "entry2IsPassword":
              this.entry2IsPassword = modalComponent[1];
              break;
            case "entry1IsPassword":
              this.entry1IsPassword = modalComponent[1];
              break;
            case "entryField1Text":
              this.entryFieldText = modalComponent[1];
              break;
            case "entryField2Text":
              this.entryField2Text = modalComponent[1];
              break;
            case "tableExists":
              this.tableExists = modalComponent[1];
              break;
            case "tableData":
              this.tableData = modalComponent[1];
              break;
            case "tableRowSeperator":
              this.tableRowSeperator = modalComponent[1];
              break;
            case "acceptMethod1":
            case "acceptMethod2":
              break;
            case "entry2ShowHide":
              this.entry2ShowHide = modalComponent[1];
              break;
            case "entry1ShowHide":
              this.entry1ShowHide = modalComponent[1];
              break;
            case "checkboxExists":
              this.checkboxExists = modalComponent[1];
              break;
            case "checkboxText":
              this.checkboxText = modalComponent[1];
            break;
            case "modalSelectExists":
              this.modalSelectExists = modalComponent[1];
              break;
            case "modalSelectOptions":
              this.modalSelectOptions = modalComponent[1];
              break;
            default:
              console.error(modalComponent[0]);
              break;
          }
        }
        // }
      );
      this.modalWindowOpen = true;
      if (this.$store.state.modalData.entryFieldExists) {
      
          this.$nextTick(() => {
            try {
              this.$refs.modalInput1.focus();
            }
            catch {
              console.log("Could not set focus!")
            }
            });

      } else if (this.$store.state.modalData.acceptButtonExists) {
        
          this.$nextTick(() => {
            try {
              this.$refs.modalAccept1.focus();
            }
            catch {
              console.log("Could not set focus!")
            }
            });
        
      }
      else if (this.$store.state.modalData.modalSelectExists) {
        
        this.$nextTick(() => {
            try {
              this.$refs.modalSelect.focus();
            }
            catch {
              console.log("Could not set focus!")
            }
        });

      }
    },
    modalAcceptMethod() {
      //This first method can take in the inputs from the entry fields
      // if (
      //   this.userResponse &&
      //   (this.userResponse.includes("'") ||
      //     this.userResponse.includes("/*") ||
      //     this.userResponse.includes("*/"))
      // ) {
      //   return console.log("Invalid Input Detected: " + this.userResponse);
      // }
      // if (
      //   this.userResponse2 &&
      //   (this.userResponse2.includes("'") ||
      //     this.userResponse2.includes("/*") ||
      //     this.userResponse2.includes("*/"))
      // ) {
      //   return console.log("Invalid Input Detected: " + this.userResponse2);
      // }
      this.$store.state.modalWindow.acceptMethod(
        this.userResponse,
        this.userResponse2,
        this.checkboxValue,
        this.modalSelectValue
      );
      this.userResponse = "";
      this.modalWindowOpen = false;
    },
    modalAcceptMethod2() {
      this.$store.state.modalWindow.acceptMethod2();
      this.modalWindowOpen = false;
    },
    closeModalWindow() {
      this.userResponse = "";
      this.modalWindowOpen = false;
    },
    // THIS SHOULD BE RUN BEFORE OR AFTER EACH MODAL WINDOW IS USED, USING eventBus.$emit("clearModalWindow");
    clearModalData() {
      this.$store.state.modalWindow = {};
      this.headerText = "";
      this.bodyText = "";
      this.acceptButtonText = "Ok";
      this.acceptButtonText = "";
      this.closeButtonText = "Close";
      this.closeButtonExists = true;
      this.acceptButtonExists = false;
      this.acceptButton2Exists = false;
      this.entryFieldExists = false;
      this.entryField2Exists = false;
      this.entryFieldText = "",
      this.entryField2Text = "",
      this.modalWindowOpen = false;
      this.userResponse = "";
      this.userResponse2 = "";
      this.bodyContainsBreaks = false;
      this.centerBodyText = false;
      this.entry2IsPassword = false;
      this.entry1IsPassword = false;
      this.acceptMethod = () => {};
      this.acceptMethod2 = () => {};
      this.tableExists = false;
      this.tableData = [];
      this.tableRowSeperator = "";
      this.entry2ShowHide = false;
      this.entry1ShowHide = false;
      this.showHideDisplay1 = "O";
      this.showHideDisplay2 = "O";
      this.checkboxValue = false;
      this.checkboxExists = false;
      this.checkboxText = "";
      this.modalSelectExists = false;
      this.modalSelectOptions = [];
      this.modalSelectValue = "";
    },
    revealPassword(entryField) {
      let x = document.getElementById(entryField);
      if (x.type === "password") {
        x.type = "text";
        if (entryField == "modal-entry-1") {
          this.showHideDisplay1 = "X";
        } else {
          this.showHideDisplay2 = "X";
        }
      } else {
        x.type = "password";
        if (entryField == "modal-entry-1") {
          this.showHideDisplay1 = "O";
        } else {
          this.showHideDisplay2 = "O";
        }
      }
    },
  },
  created() {
    eventBus.$on("clearModalData", () => {
      this.clearModalData();
    });

    eventBus.$on("openModalWindow", () => {
      this.modalWindowOpen = false;
      this.updateModaldata();
    });
  },
};
</script>

<style>
.modal-background {
  background-color: rgba(0, 0, 0, 0.7);
  height: 100%;
  width: 100%;
  z-index: 100;
  /* display: block; */
  position: fixed;
  left: 0;
  top: 0;
  justify-content: center;
  align-content: center;
}

#modal-window {
  z-index: 101;
  width: auto;
  margin: auto;
  margin: 10% 30%;
  min-width: 300px;
  background-color: rgb(100, 100, 100);
  /* border-color: rgb(70, 70, 70); */
  border: 2px solid rgb(70,70,70);
  border-radius: 10px;
}

.modal-header {
  font-weight: bold;
  font-size: 24px;
  color: black;
}

.modal-body {
  font-weight: normal;
  font-size: 16px;
  color: black;
}

#modal-window > * {
  margin: 10px;
}

.centeralign {
  text-align: center;
}

.leftalign {
  text-align: left;
}

.modal-entry {
  background-color: rgb(180, 180, 180);
  height: 20px;
  min-width: 180px;
  font-weight: bolder;
}

.modal-password {
  /* min-width: 125px; */
  margin-left: 36px;
}

.modal-button > * {
  margin: 10px;
  margin-right: 20px;
  margin-left: 20px;
  background-color: rgb(150, 150, 150);
  border-color: rgb(70, 70, 70);
  color: black;
  font-weight: bold;
  height: 30px;
  width: 20%;
  min-width: 60px;
  border-radius: 5px;
}

.modal-button > *:hover {
  border-color: black;
  background-color: rgb(180, 180, 180);
}

.modal-table {
  overflow-y: auto;
  max-height: 200px;
  border: 2px solid rgb(50, 50, 50);
}

.modal-pass-reveal {
  height: 20px;
  width: 36px;
  color: black;
  font-size: 10px;
  font-weight: bolder;
  border: none;
  background: none;
}

.modal-pass-reveal:hover {
  color: white;
}

/* This class is conditional and just centers the entry field if the show/hide is visible */
.modalCenterPass {
  margin-left: 46px;
}
/* This does not work and I am not sure why. I wanted to make the checkbox green when checked, not the default blue
#modalCheckbox:checked {
border: 1px solid green;
} */

#modalCheckboxText {
  font-weight: bold;
}

#modalSelect {
  border-radius: 5px;
  background-color: darkgrey;
  border: 1px solid black;
  width: auto;
  font-size: 14px;
}
</style>